// src/components/functions/DataSourceBlockConfig.js
import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  CircularProgress,
  Box, Typography, Button, TextField, IconButton, Checkbox,
  FormControlLabel, Select, MenuItem, Stack, Divider,
  FormControl, InputLabel, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PreviewTable from './PreviewTable';
import { db } from '../../services/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { formatText, formatTextForDatabase } from '../common/TextFormatterProvider';
import { authenticatedFetch } from '../../services/api_utils';
import { functionEndpoint } from '../../services/endpoints';
import HelpButton from '../common/HelpButton';
import { countNoun } from '../../utils/pluralise';
import AppDatePicker from '../common/AppDatePicker';
import DateDraftScope, { INCOMPLETE_DATES_MESSAGE } from '../common/DateDraftScope';
import { canonicalizeTagValue, normalizeCategoryType } from '../../utils/tagValueUtils';
import { isExternalTable, isTimeSeriesTable } from '../../utils/tableTypes';
import { isTableUsable } from '../../utils/tableLifecycle';
import { invalidDateRangeMessage, isInvertedDateRange, parameterLabel } from '../../utils/functionParameterUtils';

const DATE_RANGE_FILTERS = [
  { name: '_Start_Date', type: 'Date', special: 'startDate' },
  { name: '_End_Date', type: 'Date', special: 'endDate' },
];

const makeFilter = (column = '', type = 'Text', value = [''], variable = true) => ({
  column,
  type: normalizeCategoryType(type),
  value,
  variable,
});

const makeRangeFilter = (column, variable = true) => ({
  column,
  type: 'Date',
  range: { gte: '', lte: '' },
  variable,
});

const makeDateRangeFilters = () => [
  makeFilter('_Start_Date', 'Date', [''], true),
  makeFilter('_End_Date', 'Date', [''], true),
];

const isDateRangeColumn = (column) => ['_Start_Date', '_End_Date'].includes(column);
const isRangeFilter = (filter) => !!filter?.range && typeof filter.range === 'object' && !Array.isArray(filter.range);

// Every consumer of a data source's filters keys them by column (preview,
// execution, the run sidebar, Views, Automations), so a second filter on the
// same column would silently replace the first. One filter per column; several
// values belong in one filter's value list. Returns the first column chosen by
// more than one filter, or null.
const findDuplicateFilterColumn = (filters) => {
  const seen = new Set();
  for (const filter of filters) {
    if (!filter.column) continue;
    const key = formatTextForDatabase(filter.column);
    if (seen.has(key)) return filter.column;
    seen.add(key);
  }
  return null;
};

const getEmptyInputValue = (type) => (normalizeCategoryType(type) === 'Yes/No' ? '' : '');

const normalizeLoadedValue = (value, type) => {
  const declaredType = normalizeCategoryType(type);
  if (value === null || value === undefined || value === '') {
    return getEmptyInputValue(declaredType);
  }
  if (declaredType === 'Yes/No') {
    if (typeof value === 'boolean') return value;
    const normalized = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    return '';
  }
  return String(value);
};

const normalizeFilterValues = (values, type) => {
  const declaredType = normalizeCategoryType(type);
  return (Array.isArray(values) ? values : [values])
    .map((value) => {
      if (value === null || value === undefined) return null;
      if (declaredType === 'Text') {
        const normalized = String(value).trim();
        return normalized === '' ? null : normalized;
      }
      if (declaredType === 'Yes/No') {
        if (value === '') return null;
        return canonicalizeTagValue(value, declaredType);
      }
      if (String(value).trim() === '') return null;
      return canonicalizeTagValue(value, declaredType);
    })
    .filter((value) => value !== null);
};

const DataSourceBlockConfig = forwardRef(function DataSourceBlockConfig({
  companyName,
  functionId,
  blockId,
  tables = [],
  onSave,
  onCancel,
}, ref) {
  const [name, setName] = useState('');
  const [tableId, setTableId] = useState('');
  const [filters, setFilters] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  // The backend cut the preview down to a sample (`truncated` in its
  // response); a run reads every matching row (M230).
  const [previewTruncated, setPreviewTruncated] = useState(false);
  // Why the last preview failed (the server's message), shown in the preview
  // pane in place of the table. A failed preview never counts as fresh.
  const [previewError, setPreviewError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState('');
  // Half-typed dates in the filters (see AppDatePicker): Preview and Save refuse them.
  const dateScopeRef = useRef(null);

  const [detailColumns, setDetailColumns] = useState([]);
  const [attributeColumns, setAttributeColumns] = useState([]);
  // The row key, offered as a filter column like a detail column (M65). An
  // external table's key is already one of its mapped columns.
  const [identifierColumn, setIdentifierColumn] = useState(null);
  const [selectedTableDoc, setSelectedTableDoc] = useState(null);
  const [previewFresh, setPreviewFresh] = useState(false);
  const [userChangedTable, setUserChangedTable] = useState(false);
  const userEditedRef = useRef(false);
  const tablesRef = useRef(tables);
  tablesRef.current = tables;
  const [blockLoading, setBlockLoading] = useState(true);
  const [blockLoaded, setBlockLoaded] = useState(false);
  const [tableAvailability, setTableAvailability] = useState('checking');
  // A preview belongs to the configuration it was requested for. Every change
  // to the table or filters bumps the generation and aborts the request in
  // flight, so a response for an earlier configuration can never install its
  // rows — or mark the editor fresh — over the edited one.
  const previewGenerationRef = useRef(0);
  const previewAbortRef = useRef(null);

  const invalidatePreview = useCallback(() => {
    previewGenerationRef.current += 1;
    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
      previewAbortRef.current = null;
    }
    setPreviewFresh(false);
    setPreviewData(null);
    setPreviewTruncated(false);
    setPreviewError(null);
    setLoadingPreview(false);
  }, []);

  useEffect(() => () => {
    if (previewAbortRef.current) previewAbortRef.current.abort();
  }, []);

  const supportsDateRangeFilters = !!tableId && !!selectedTableDoc && isTimeSeriesTable(selectedTableDoc);

  // The live table catalogue populates the picker; its identity must never
  // cause the saved block to reload over this editor's unsaved changes.
  const defaultFiltersForTable = useCallback((nextTableId, tableDoc = null) => {
    if (!nextTableId) return [];
    const nextTable = tableDoc || tablesRef.current.find(table => table.id === nextTableId);
    return nextTable && isTimeSeriesTable(nextTable) ? makeDateRangeFilters() : [];
  }, []);

  const columnDefinitions = useMemo(() => {
    const schemaColumns = [
      ...(identifierColumn ? [identifierColumn] : []),
      ...detailColumns,
      ...attributeColumns,
    ].map((column) => ({ name: column.name, type: normalizeCategoryType(column.type) }));
    return [...(supportsDateRangeFilters ? DATE_RANGE_FILTERS : []), ...schemaColumns];
  }, [identifierColumn, detailColumns, attributeColumns, supportsDateRangeFilters]);

  const columnTypeMap = useMemo(() => {
    const map = {};
    columnDefinitions.forEach((column) => {
      map[column.name] = column.type;
    });
    return map;
  }, [columnDefinitions]);

  const resolveColumnType = useCallback((columnName, fallback = 'Text') => {
    if (!columnName) return normalizeCategoryType(fallback);
    return columnTypeMap[columnName] || normalizeCategoryType(fallback);
  }, [columnTypeMap]);

  useEffect(() => {
    let cancelled = false;
    userEditedRef.current = false;
    setBlockLoading(true);
    setBlockLoaded(false);
    const loadBlock = async () => {
      if (!companyName || !functionId || !blockId) return;
      const blockRef = doc(db, `users/${companyName}/data_functions/${functionId}/blocks`, blockId);
      const blockSnap = await getDoc(blockRef);
      if (cancelled) return;
      if (!blockSnap.exists()) throw new Error('This source block no longer exists.');
      const data = blockSnap.data();
      const nextTableId = data.config?.table_id || '';
      setName(formatText(data.name || ''));
      setTableId(nextTableId);
      const loadedFilters = (data.config?.filters || []).map((filter) => {
        const column = filter.column || '';
        const declaredType = normalizeCategoryType(filter.type || (isDateRangeColumn(column) ? 'Date' : 'Text'));
        const variable = !!filter.variable;
        if (isRangeFilter(filter) && !isDateRangeColumn(column)) {
          return {
            ...makeRangeFilter(column, variable),
            range: {
              gte: filter.range.gte || '',
              lte: filter.range.lte || '',
            },
          };
        }

        const rawStoredValues = Array.isArray(filter.value) ? filter.value : [];
        if (declaredType === 'Date' && !isDateRangeColumn(column)) {
          throw new Error(`${parameterLabel(column)} requires a Date range. Migrate the saved filter before editing.`);
        }

        const storedValues = rawStoredValues.length > 0 ? rawStoredValues : [''];
        return makeFilter(
          column,
          declaredType,
          storedValues.map((value) => normalizeLoadedValue(value, declaredType)),
          variable,
        );
      });

      if (loadedFilters.length === 0) {
        setFilters(defaultFiltersForTable(nextTableId));
      } else {
        setFilters(loadedFilters);
      }
      setLoadedGeneration((n) => n + 1);
      setBlockLoaded(true);
    };
    loadBlock().catch(error => {
      if (!cancelled) setError(`Could not load this source: ${error.message}`);
    }).finally(() => {
      if (!cancelled) setBlockLoading(false);
    });
    return () => { cancelled = true; };
  }, [companyName, functionId, blockId, defaultFiltersForTable]);

  useEffect(() => {
    if (!userChangedTable) return;
    invalidatePreview();
    setFilters(defaultFiltersForTable(tableId));
    setUserChangedTable(false);
  }, [tableId, userChangedTable, defaultFiltersForTable, invalidatePreview]);

  useEffect(() => {
    if (!companyName || !tableId) {
      setDetailColumns([]);
      setAttributeColumns([]);
      setIdentifierColumn(null);
      setSelectedTableDoc(null);
      return;
    }

    setSelectedTableDoc(null);
    setTableAvailability('checking');
    let cancelled = false;
    const tableRef = doc(db, `users/${companyName}/tables`, tableId);
    const stopAvailability = onSnapshot(tableRef, snapshot => {
      if (cancelled) return;
      const available = snapshot.exists() && isTableUsable(snapshot.data());
      setTableAvailability(available ? 'available' : 'unavailable');
      if (!available) invalidatePreview();
    }, () => {
      if (!cancelled) setTableAvailability('error');
    });
    (async () => {
      const docRef = doc(db, `users/${companyName}/tables`, tableId);
      const docSnap = await getDoc(docRef);
      if (!cancelled && docSnap.exists()) {
        const tableDoc = { id: docSnap.id, ...docSnap.data() };
        setSelectedTableDoc(tableDoc);
        setDetailColumns(tableDoc.detailColumns || []);
        setAttributeColumns([...(tableDoc.attributeColumns || []), ...(tableDoc.tableAttributeColumns || [])]);
        setIdentifierColumn((!isExternalTable(tableDoc) && tableDoc.identifier?.name)
          ? { name: tableDoc.identifier.name, type: tableDoc.identifier.type || 'Text' }
          : null);
        setFilters((prev) => {
          if (isTimeSeriesTable(tableDoc)) {
            return prev.length === 0 ? makeDateRangeFilters() : prev;
          }
          return prev.filter((filter) => !isDateRangeColumn(filter.column));
        });
        // Reconciling the stored filters against the table is part of loading,
        // not an edit — unless the table is one the user has just picked.
        if (!userEditedRef.current) setLoadedGeneration((n) => n + 1);
      }
    })().catch(error => {
      if (!cancelled) setError(`Could not load the table schema: ${error.message}`);
    });
    return () => {
      cancelled = true;
      stopAvailability();
    };
  }, [companyName, tableId, invalidatePreview]);

  useEffect(() => {
    if (columnDefinitions.length === 0) return;
    setFilters((prev) => prev.map((filter) => {
      const nextType = resolveColumnType(filter.column, filter.type);
      if (nextType === filter.type) return filter;
      if (nextType === 'Date' && !isDateRangeColumn(filter.column)) {
        return makeRangeFilter(filter.column, filter.variable);
      }
      return makeFilter(
        filter.column,
        nextType,
        (filter.value || ['']).map((value) => normalizeLoadedValue(value, nextType)),
        filter.variable,
      );
    }));
    if (!userEditedRef.current) setLoadedGeneration((n) => n + 1);
  }, [columnDefinitions, resolveColumnType]);

  const handleAddFilter = () => {
    userEditedRef.current = true;
    setFilters((prev) => [...prev, makeFilter('', 'Text', [''], true)]);
    invalidatePreview();
  };

  const handleRemoveFilter = (index) => {
    userEditedRef.current = true;
    setFilters((prev) => prev.filter((_, i) => i !== index));
    invalidatePreview();
  };

  const handleFilterChange = (index, key, value) => {
    userEditedRef.current = true;
    setFilters((prev) => {
      const nextFilters = [...prev];
      const current = { ...nextFilters[index] };

      if (key === 'column') {
        const nextType = resolveColumnType(value, current.type);
        nextFilters[index] = nextType === 'Date' && !isDateRangeColumn(value)
          ? makeRangeFilter(value, current.variable)
          : makeFilter(value, nextType, [getEmptyInputValue(nextType)], current.variable);
      } else {
        nextFilters[index] = { ...current, [key]: value };
      }

      return nextFilters;
    });
    invalidatePreview();
  };

  const handleFilterValueChange = (index, valueIndex, newValue) => {
    userEditedRef.current = true;
    setFilters((prev) => {
      const nextFilters = [...prev];
      const current = { ...nextFilters[index] };
      const values = [...(current.value || [''])];
      values[valueIndex] = newValue;
      current.value = values;
      nextFilters[index] = current;
      return nextFilters;
    });
    invalidatePreview();
  };

  const handleFilterRangeChange = (index, key, value) => {
    userEditedRef.current = true;
    setFilters((prev) => {
      const nextFilters = [...prev];
      const current = { ...nextFilters[index] };
      current.range = { ...(current.range || { gte: '', lte: '' }), [key]: value };
      nextFilters[index] = current;
      return nextFilters;
    });
    invalidatePreview();
  };

  const handleAddFilterValue = (index) => {
    userEditedRef.current = true;
    setFilters((prev) => {
      const nextFilters = [...prev];
      const current = { ...nextFilters[index] };
      current.value = [...(current.value || []), getEmptyInputValue(current.type)];
      nextFilters[index] = current;
      return nextFilters;
    });
    invalidatePreview();
  };

  const handleRemoveFilterValue = (index, valueIndex) => {
    userEditedRef.current = true;
    setFilters((prev) => {
      const nextFilters = [...prev];
      const current = { ...nextFilters[index] };
      current.value = (current.value || []).filter((_, i) => i !== valueIndex);
      if (current.value.length === 0) {
        current.value = [getEmptyInputValue(current.type)];
      }
      nextFilters[index] = current;
      return nextFilters;
    });
    invalidatePreview();
  };

  // Resolves with the rows it installed, or null when nothing was installed (a
  // validation failure, a failed fetch, or a request superseded by a newer one) —
  // so Save can run the preview it needs and use the result in the same call,
  // where React state would still be the pre-preview value.
  // A fixed Start Date after a fixed End Date, or a range whose From is after
  // its To, is refused before any query runs (M41). Variable filters are
  // checked again by the rail that fills them.
  const invertedDateMessage = () => {
    const single = (name) => normalizeFilterValues(
      (filters.find((filter) => filter.column === name) || {}).value || [], 'Date')[0];
    if (supportsDateRangeFilters && isInvertedDateRange(single('_Start_Date'), single('_End_Date'))) {
      return invalidDateRangeMessage();
    }
    const range = filters.find((filter) => isRangeFilter(filter) && isInvertedDateRange(filter.range.gte, filter.range.lte));
    return range ? invalidDateRangeMessage(range.column) : null;
  };

  const handlePreview = async () => {
    if (!blockLoaded || !tableId || !selectedTableDoc || tableAvailability !== 'available') {
      setError('Select an available table before previewing.');
      return null;
    }
    const duplicateColumn = findDuplicateFilterColumn(filters);
    if (duplicateColumn) {
      setError(`Column '${duplicateColumn}' is used by more than one filter. Remove one, and add extra values to the other filter instead.`);
      return null;
    }
    if (dateScopeRef.current?.reveal()) {
      setError(INCOMPLETE_DATES_MESSAGE);
      return null;
    }
    const invertedDates = invertedDateMessage();
    if (invertedDates) {
      setError(invertedDates);
      return null;
    }

    setError('');
    // Only a completed, successful preview of the current settings is fresh;
    // the previous one stops counting the moment a new attempt starts.
    invalidatePreview();
    const generation = previewGenerationRef.current;
    const controller = new AbortController();
    previewAbortRef.current = controller;
    setLoadingPreview(true);

    try {
      const filterObj = {};
      const rangeFiltersObj = {};
      let startDateFilter = '';
      let endDateFilter = '';

      for (const filter of filters) {
        if (!filter.column) continue;
        if (isRangeFilter(filter)) {
          const range = {};
          if (String(filter.range.gte || '').trim() !== '') range.gte = filter.range.gte;
          if (String(filter.range.lte || '').trim() !== '') range.lte = filter.range.lte;
          if (Object.keys(range).length > 0) {
            rangeFiltersObj[formatTextForDatabase(filter.column)] = range;
          }
          continue;
        }
        const normalizedValues = normalizeFilterValues(filter.value, filter.type);

        if (supportsDateRangeFilters && filter.column === '_Start_Date') {
          startDateFilter = normalizedValues[0] || '';
        } else if (supportsDateRangeFilters && filter.column === '_End_Date') {
          endDateFilter = normalizedValues[0] || '';
        } else if (normalizedValues.length > 0) {
          filterObj[formatTextForDatabase(filter.column)] = normalizedValues;
        }
      }

      if (supportsDateRangeFilters) {
        if (startDateFilter) filterObj.startDate = startDateFilter;
        if (endDateFilter) filterObj.endDate = endDateFilter;
      }

      const response = await authenticatedFetch(
        functionEndpoint('get_table_data'),
        {
          method: 'POST',
          body: JSON.stringify({
            company_id: companyName,
            table_id: tableId,
            preview: true,
            filters: filterObj,
            range_filters: rangeFiltersObj,
          }),
          signal: controller.signal,
        },
      );
      // authenticatedFetch returns the response whatever its status. An external
      // table's upstream failure is a 502 with a JSON body {"error": …}; a filter
      // validation failure is a 400 with a plain-text body. Neither is a preview.
      if (!response.ok) {
        const bodyText = await response.text();
        let message = bodyText;
        try {
          const parsed = JSON.parse(bodyText);
          if (parsed && typeof parsed.error === 'string') message = parsed.error;
        } catch (parseErr) {
          // Plain-text body: shown as is.
        }
        throw new Error(message || `Request failed with status ${response.status}`);
      }
      const data = await response.json();
      if (generation !== previewGenerationRef.current) return null;
      const rows = data.rows || [];
      setPreviewData(rows);
      setPreviewTruncated(data.truncated === true);
      setPreviewFresh(true);
      return rows;
    } catch (err) {
      // A superseded request (aborted by an edit or a newer preview) says
      // nothing: the editor already reflects the newer configuration. An abort
      // on unmount is not an error either.
      if (generation !== previewGenerationRef.current || err?.name === 'AbortError') return null;
      console.error('Preview error:', err);
      setPreviewError(err.message || 'Unknown error');
      return null;
    } finally {
      if (generation === previewGenerationRef.current) {
        previewAbortRef.current = null;
        setLoadingPreview(false);
      }
    }
  };

  const handleSave = async () => {
    if (!blockLoaded || !name.trim() || !tableId || !selectedTableDoc || tableAvailability !== 'available') {
      setError('Provide a name and select an available table.');
      return false;
    }
    const duplicateColumn = findDuplicateFilterColumn(filters);
    if (duplicateColumn) {
      setError(`Column '${duplicateColumn}' is used by more than one filter. Remove one, and add extra values to the other filter instead.`);
      return false;
    }
    if (dateScopeRef.current?.reveal()) {
      setError(INCOMPLETE_DATES_MESSAGE);
      return false;
    }
    const invertedDates = invertedDateMessage();
    if (invertedDates) {
      setError(invertedDates);
      return false;
    }
    // The saved block carries a preview of its current settings. One that has
    // not been run since the last change is run here rather than demanded of
    // the user; a preview that fails leaves its message in the preview pane and
    // nothing is saved.
    let rows = previewData;
    if (!previewFresh) {
      rows = await handlePreview();
      if (!rows) return false;
    }

    const finalFilters = filters
      .map((filter) => {
        if (!filter.column) return null;
        if (!supportsDateRangeFilters && isDateRangeColumn(filter.column)) return null;
        if (isRangeFilter(filter)) {
          const range = {
            gte: filter.range.gte || '',
            lte: filter.range.lte || '',
          };
          if (!filter.variable && !range.gte && !range.lte) return null;
          return {
            column: formatTextForDatabase(filter.column),
            type: 'Date',
            range,
            variable: filter.variable,
          };
        }
        const normalizedValues = normalizeFilterValues(filter.value, filter.type);
        if (!filter.variable && normalizedValues.length === 0) return null;

        return {
          column: formatTextForDatabase(filter.column),
          type: normalizeCategoryType(filter.type),
          value: normalizedValues,
          variable: filter.variable,
        };
      })
      .filter(Boolean);

    const config = {
      name: formatTextForDatabase(name.trim()),
      config: {
        table_id: tableId,
        filters: finalFilters,
      },
      preview_data: rows,
    };
    return onSave(blockId, config);
  };

  // The builder's Publish and Close ask this editor two things through its ref:
  // whether what is on screen differs from what was loaded, and to save it.
  // The baseline is the editable state as it stood when loading finished.
  const [loadedGeneration, setLoadedGeneration] = useState(0);
  const baselineRef = useRef('');
  const editSignature = JSON.stringify({ name, tableId, filters });
  useEffect(() => {
    if (!userEditedRef.current) baselineRef.current = editSignature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedGeneration]);
  useImperativeHandle(ref, () => ({
    isDirty: () => editSignature !== baselineRef.current,
    save: () => Promise.resolve(handleSave()),
  }));

  const renderValueInput = (filter, value, index, valueIndex) => {
    const declaredType = normalizeCategoryType(filter.type);
    const commonProps = {
      fullWidth: true,
      size: 'small',
    };

    if (declaredType === 'Date') {
      return (
        <AppDatePicker
          {...commonProps}
          value={value}
          onChange={(nextDate) => handleFilterValueChange(index, valueIndex, nextDate)}
        />
      );
    }

    if (declaredType === 'Number') {
      return (
        <TextField
          {...commonProps}
          type="number"
          value={value ?? ''}
          onChange={(e) => handleFilterValueChange(index, valueIndex, e.target.value)}
        />
      );
    }

    if (declaredType === 'Yes/No') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Value</InputLabel>
          <Select
            value={value === true ? 'true' : value === false ? 'false' : ''}
            label="Value"
            onChange={(e) => {
              if (e.target.value === '') {
                handleFilterValueChange(index, valueIndex, '');
                return;
              }
              handleFilterValueChange(index, valueIndex, e.target.value === 'true');
            }}
          >
            <MenuItem value="">
              <em>Unset</em>
            </MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        {...commonProps}
        value={value || ''}
        onChange={(e) => handleFilterValueChange(index, valueIndex, e.target.value)}
      />
    );
  };

  return (
    <DateDraftScope ref={dateScopeRef}>
    <Box sx={{
      p: 2,
      // The settings column keeps its 500px; the preview column is twice that,
      // hence 1500px and the 4/8 split below. maxWidth leaves the same 3.3% gap
      // on the right as `left` does on the left, so a dialog wider than the
      // window shrinks instead of running off the edge.
      width: (previewData || previewError) ? '1500px' : '600px',
      maxWidth: 'calc(100% - 6.6%)',
      maxHeight: '80vh',
      position: 'absolute',
      top: 100,
      left: '3.3%',
      zIndex: 999,
      background: '#1A1E26',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: 1,
      color: '#F0F2F5',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      borderTop: '3px solid #2EB998',
      transition: 'width 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid item xs={(previewData || previewError) ? 4 : 12} sx={{ height: '100%', maxHeight: '78vh', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ color: '#2EB998' }}>Configure Data Source Block</Typography>
            <HelpButton
              docPath="dashboard-page/data-functions/building-your-first-function/adding-data-source-node"
              tooltip="Data Source Documentation"
            />
          </Box>
          <TextField fullWidth size="small" label="Name" value={name} disabled={blockLoading || !blockLoaded} onChange={(e) => { userEditedRef.current = true; setName(e.target.value); }} sx={{ mb: 2 }} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="source-table-label">Select a table...</InputLabel>
            <Select labelId="source-table-label" value={tableId} disabled={blockLoading || !blockLoaded} label="Select a table..." onChange={(e) => {
              userEditedRef.current = true;
              setSelectedTableDoc(null);
              setUserChangedTable(true);
              setTableId(e.target.value);
            }}>
              <MenuItem value=""><em>Select a table...</em></MenuItem>
              {tables.map((table) => (
                <MenuItem key={table.id} value={table.id}>{formatText(table.name || table.id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Filters</Typography>
          <Box component="fieldset" disabled={blockLoading || !blockLoaded || !selectedTableDoc} sx={{ border: 0, p: 0, m: 0 }}>
          <Stack spacing={2}>
            {filters.map((filter, index) => (
              <Box key={index} sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', p: 2, borderRadius: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography>Filter {index + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => handleRemoveFilter(index)}>
                    <RemoveIcon />
                  </IconButton>
                </Stack>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Column</InputLabel>
                  <Select value={filter.column} label="Column" onChange={(e) => handleFilterChange(index, 'column', e.target.value)}>
                    {columnDefinitions
                      // A column already chosen by another filter is not offered
                      // again; this filter's own choice stays listed.
                      .filter((column) => column.name === filter.column
                        || !filters.some((other, otherIndex) => otherIndex !== index && other.column === column.name))
                      .map((column) => (
                        <MenuItem key={column.name} value={column.name}>{parameterLabel(column.name)}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Type: {normalizeCategoryType(filter.type)}
                </Typography>
                {isRangeFilter(filter) ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <AppDatePicker
                      fullWidth
                      label="From"
                      value={filter.range.gte}
                      onChange={(nextDate) => handleFilterRangeChange(index, 'gte', nextDate)}
                    />
                    <AppDatePicker
                      fullWidth
                      label="To"
                      value={filter.range.lte}
                      onChange={(nextDate) => handleFilterRangeChange(index, 'lte', nextDate)}
                    />
                  </Stack>
                ) : (
                  <>
                    {(filter.value || []).map((value, valueIndex) => (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} key={valueIndex}>
                        <Box sx={{ flex: 1 }}>
                          {renderValueInput(filter, value, index, valueIndex)}
                        </Box>
                        <IconButton size="small" onClick={() => handleRemoveFilterValue(index, valueIndex)} color="error">
                          <RemoveIcon fontSize="inherit" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => handleAddFilterValue(index)}>
                      Add Value
                    </Button>
                  </>
                )}
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={<Checkbox checked={filter.variable} onChange={(e) => handleFilterChange(index, 'variable', e.target.checked)} />}
                  label="Make this filter variable"
                />
              </Box>
            ))}
          </Stack>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={handleAddFilter} disabled={!tableId}>
            Add Filter
          </Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          {tableId && tableAvailability === 'unavailable' && <Typography color="error">This table is missing or being deleted. Select another table.</Typography>}
          {tableId && tableAvailability === 'error' && <Typography color="error">Table availability could not be checked. Reopen this editor to retry.</Typography>}
          {/* Messages sit with the buttons they answer, so a long filter list never scrolls them out of view. */}
          {error && <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" onClick={handleSave} disabled={loadingPreview || blockLoading || !blockLoaded || !selectedTableDoc || tableAvailability !== 'available'}>
              {loadingPreview ? 'Previewing…' : 'Save'}
            </Button>
            <Button variant="contained" color="secondary" onClick={handlePreview} disabled={loadingPreview || blockLoading || !blockLoaded || !selectedTableDoc || tableAvailability !== 'available'}>
              Preview
            </Button>
            <Button variant="text" onClick={onCancel}>Cancel</Button>
            {loadingPreview && <CircularProgress size={18} />}
          </Stack>
        </Grid>
        {previewError && !previewData && (
          <Grid item xs={8} sx={{ height: '100%', maxHeight: '78vh', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: '#2EB998', position: 'sticky', top: 0, backgroundColor: '#1A1E26', zIndex: 2, py: 1 }}>Preview Data</Typography>
            <Box sx={{ flexGrow: 1, border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 1, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Typography variant="body1" color="error" sx={{ mb: 1 }}>
                The preview fetch failed. Please retry. If this does not work, please contact support.
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(240, 242, 245, 0.6)', mb: 2, wordBreak: 'break-word' }}>
                {previewError}
              </Typography>
              <Button variant="outlined" onClick={handlePreview} disabled={loadingPreview || blockLoading || !blockLoaded || !selectedTableDoc || tableAvailability !== 'available'}>Retry</Button>
            </Box>
          </Grid>
        )}
        {previewData && (
          <Grid item xs={8} sx={{ height: '100%', maxHeight: '78vh', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: '#2EB998', position: 'sticky', top: 0, backgroundColor: '#1A1E26', zIndex: 2, py: 1 }}>Preview Data</Typography>
            {previewTruncated && previewData.length > 0 && (
              <Typography variant="body2" sx={{ mb: 1, color: 'warning.main' }}>
                {`Sample of ${countNoun(previewData.length, 'row')}. Runs read every matching row, up to 50,000.`}
              </Typography>
            )}
            <Box sx={{ flexGrow: 1, border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 1 }}>
              <PreviewTable data={previewData} tableSchema={selectedTableDoc} truncated={previewTruncated} />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
    </DateDraftScope>
  );
});

export default DataSourceBlockConfig;
